import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse } from 'react-router-dom';

import { isFetchBaseQueryError } from '../api/error';
import { isDynamicImportError } from '../utils/error';

export const useErrorMessage = (
  error: unknown
): { title: string | undefined; message: string | undefined } | null => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (error == null) return null;

    if (isRouteErrorResponse(error)) {
      if (error.status === 404) {
        return { title: undefined, message: t('Error.status.404.text') };
      }
      return { title: undefined, message: undefined };
    }

    if (typeof error === 'string') {
      return { title: undefined, message: error };
    }

    if (isDynamicImportError(error)) {
      return {
        title: t('error.dynamicImport.title'),
        message: t('error.dynamicImport.message')
      };
    }

    if (!isFetchBaseQueryError(error)) {
      return { title: t('error.occurred'), message: t('error.unknown') };
    }

    if (error.status === 'FETCH_ERROR') {
      return {
        title: t('error.network.title'),
        message: t('error.network.message')
      };
    }
    if (error.status === 'TIMEOUT_ERROR') {
      return {
        title: t('error.timeout.title'),
        message: t('error.timeout.message')
      };
    }
    if (error.status === 'PARSING_ERROR') {
      return {
        title: t('error.parsing.title'),
        message: t('error.parsing.message')
      };
    }

    // Numeric HTTP error — use backend message if available
    const msg = (error.data as { message?: string } | undefined)?.message;
    return { title: t('error.occurred'), message: msg ?? t('error.unknown') };
  }, [error, t]);
};
